import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ListGroup } from 'react-bootstrap';
import {
  FaCalendarAlt, FaUserFriends, FaBuilding, FaCogs, FaChartBar,
  FaEnvelope, FaWhatsapp, FaVideo, FaAd, FaClipboardList, FaFlag
} from 'react-icons/fa';
import { useWhatsAppMessage } from '../../context/WhatsAppMessageContext';
import UserSessionManager from '../../utils/UserSessionManager';
import FeatureFlagService, { FeatureFlags } from '../../services/FeatureFlagService';

const Sidebar = () => {
  const location = useLocation();
  const { totalUnreadCount } = useWhatsAppMessage();
  const isOnWhatsAppPage = location.pathname.startsWith('/whatsapp');

  // fetchAndCache() (login, app boot) can resolve after this component has
  // already mounted and filtered flag-gated tabs out — re-render once it does
  // so a tab like Email doesn't stay hidden until some unrelated re-render.
  const [, forceRerender] = useState(0);
  useEffect(() => FeatureFlagService.onUpdate(() => forceRerender((n) => n + 1)), []);
  // Feature flags are administered against the single Prod DB, so the Features
  // tab is shown only to super admins on the Prod deployment (REACT_APP_ENV).
  const showFeatures = UserSessionManager.isSuperAdmin() && process.env.REACT_APP_ENV === 'Prod';

  const menuItems = [
    { label: 'Dashboard', icon: <FaChartBar />, path: '/dashboard' },
    { label: 'Events', icon: <FaCalendarAlt />, path: '/events' },
    { label: 'Leads', icon: <FaUserFriends />, path: '/leads' },
    { label: 'Accounts', icon: <FaBuilding />, path: '/accounts' },
    { label: 'Automation', icon: <FaCogs />, path: '/automation' },
    { label: 'Growth', icon: <FaChartBar />, path: '/growth' },
  ];

  const integrations = [
    { label: 'Email', icon: <FaEnvelope />, path: '/email', flag: FeatureFlags.EMAIL_V2 },
    { label: 'WhatsApp', icon: <FaWhatsapp />, path: '/whatsapp' },
    { label: 'Video Conf.', icon: <FaVideo />, path: '/video-conf' },
    { label: 'Ads Compose', icon: <FaAd />, path: '/ads-compose', flag: FeatureFlags.ADS_COMPOSE_TAB },
    { label: 'Calendar', icon: <FaClipboardList />, path: '/calendar' },
    { label: 'Templates', icon: <FaClipboardList />, path: '/templates' },
  ].filter((item) => !item.flag || FeatureFlagService.isEnabled(item.flag));

  return (
    <div
      style={{
        width: '220px',
        background: 'linear-gradient(to right, #E4E5EB 80%, #ffffff 100%)',
        minHeight: '100vh',
        padding: '20px',
        marginLeft: '10px',
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
        boxShadow: '2px 0 5px rgba(0,0,0,0.05)'
      }}
    >
      <ListGroup style={{ border: 'none' }}>
        {menuItems.map((item, idx) => (
          <ListGroup.Item
            key={idx}
            as={NavLink}
            to={item.path}
            className="sidebar-item d-flex align-items-center"
          >
            <span className="me-2">{item.icon}</span> {item.label}
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* Darker Divider */}
      <hr style={{ margin: '20px 0', borderTop: '2px solid #b0b0b0' }} />

      {/* Subheading for Integrations */}
      <div className="d-flex align-items-center mb-2 text-muted" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
        <FaCogs className="me-2" /> Integrations
      </div>

      <ListGroup style={{ border: 'none' }}>
        {integrations.map((item, idx) => (
          <ListGroup.Item
            key={idx}
            as={NavLink}
            to={item.path}
            className="sidebar-item d-flex align-items-center"
            style={{ position: 'relative' }}
          >
            <span className="me-2">{item.icon}</span> {item.label}
            {item.badge && (
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  backgroundColor: '#2355e0',
                  color: 'white',
                  borderRadius: '4px',
                  padding: '1px 5px',
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.04em',
                }}
              >
                {item.badge}
              </span>
            )}
            {item.path === '/whatsapp' && !isOnWhatsAppPage && totalUnreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  backgroundColor: '#B2400B',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </span>
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* Admin-only section: Feature Flags (super admins, Prod only) */}
      {showFeatures && (
        <>
          {/* Darker Divider */}
          <hr style={{ margin: '20px 0', borderTop: '2px solid #b0b0b0' }} />

          <ListGroup style={{ border: 'none' }}>
            <ListGroup.Item
              as={NavLink}
              to="/features"
              className="sidebar-item d-flex align-items-center"
            >
              <span className="me-2"><FaFlag /></span> Features
            </ListGroup.Item>
          </ListGroup>
        </>
      )}
    </div>
  );
};

export default Sidebar;
